import { createElement, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useGenericAssistantPanel } from "../../../contexts/AssistantPanelContext";
import { useCommandRegistry } from "../../../contexts/CommandRegistryContext";
import { MAGICBI_LOGO_SRC } from "../../../constants/brandAssets";
import { screenLabelForPath } from "../../../services/commands/screenLabel";
import type { CommandContext } from "../../../services/commands/commands.types";
import type { CommandListGroup } from "../../molecules/CommandList";
import { SCREEN_ASSISTANT_COPY } from "./ScreenAssistantPanel.copy";

const ACTION_PREFIX = "action:";
const ASSISTANT_CONTEXT: CommandContext = { source: "chat" };

/** State of the generic MagicBI panel: ready commands of this screen, the typed request and its result line. */
export const useScreenAssistantPanelInternalState = () => {
  const { visible, close } = useGenericAssistantPanel();
  const registry = useCommandRegistry();
  const { pathname } = useLocation();
  const [text, setText] = useState("");
  const [result, setResult] = useState("");
  const [, setVersion] = useState(0);

  // Re-read the list after a run, since availability may have changed.
  const bump = useCallback(() => setVersion((v) => v + 1), []);
  useEffect(() => { if (!visible) { setText(""); setResult(""); } }, [visible]);

  const ready = visible
    ? registry.list().filter((action) => {
        try { return action.isAvailable(ASSISTANT_CONTEXT).status === "ready" && action.id !== "palette.open" && action.id !== "app.ask"; } catch { return false; }
      })
    : [];

  const groups = useMemo<CommandListGroup[]>(() => [{
    id: "screen",
    items: ready.map((action) => ({
      id: `${ACTION_PREFIX}${action.id}`,
      label: action.label,
      hint: action.hint,
      icon: action.icon
        ? createElement(action.icon, { size: 14, "aria-hidden": true })
        : createElement("img", { src: MAGICBI_LOGO_SRC, alt: "", width: 14, height: 14 }),
    })),
  }], [ready.map((a) => a.id).join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  const runAction = async (id: string): Promise<void> => {
    const outcome = await registry.run(id.replace(ACTION_PREFIX, ""), {}, ASSISTANT_CONTEXT);
    setResult(outcome.message);
    bump();
  };

  const send = async (): Promise<void> => {
    const value = text.trim();
    if (!value) return;
    const resolution = await registry.resolve(value, ASSISTANT_CONTEXT);
    if (resolution.kind === "intent") {
      const outcome = await registry.run(resolution.intent.actionId, resolution.intent.args, ASSISTANT_CONTEXT);
      setResult(outcome.message);
    } else {
      setResult(resolution.kind === "ambiguous" ? resolution.message : SCREEN_ASSISTANT_COPY.noMatch);
    }
    setText("");
    bump();
  };

  return { visible, close, groups, screenLabel: screenLabelForPath(pathname), text, setText, result, runAction, send };
};
